import { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import WithMovie, { movieContext } from '@hoc/WithMovie'
import Autocomplete, { KeyValue } from '@components/Autocomplete/Autocomplete'
import { MovieProps } from '../AnswerProps'
import Select from '@components/Select/Select'
import { usePixelateImage } from '@hooks/usePixelate'
import { MimeType } from '@helpers/pixelate'
import Image from '@components/Image/Image'
import defaultMovieImage from './no-movie.jpg'
import defaultMovieImageWebp from './no-movie.webp'
import defaultMovieImageAvif from './no-movie.avif'
import './MovieInput.scss'

export const MovieInput: FunctionComponent<MovieProps> = ({ onSelection, answer }: MovieProps) => {
  const { t, i18n } = useTranslation('Movie')
  const { searchMovie, selectMovie, errorMessage, movies, selectedMovie, genres, selectGenre, selectedGenre } =
    useContext(movieContext)

  const pixelatedArtworkUrl = usePixelateImage(answer?.artworkUrl, MimeType.JPEG)

  useEffect(() => {
    if (answer) {
      selectMovie(answer)
    }
  }, [])

  useEffect(() => {
    if (selectedMovie) {
      onSelection({
        ...selectedMovie,
        genre: selectedGenre,
        pixelatedArtworkUrl,
      })
    }
  }, [selectedGenre, selectedMovie, pixelatedArtworkUrl])

  const searchResultItems = useMemo(
    () =>
      movies.map((m) => {
        return {
          value: m.id,
          label: m.title,
          movie: m,
        }
      }),
    [movies],
  )

  const genreResultItems = useMemo(
    () =>
      genres.map((g) => {
        return {
          value: g,
          label: t(g.toUpperCase()),
        }
      }),
    [genres],
  )

  const onChangeCallback = useCallback(
    (id: string | undefined) => {
      if (movies && id) {
        const movie = movies.find((m) => m.id === id)
        selectMovie(movie)
      }
    },
    [movies],
  )

  const onClickGenre = (item: KeyValue | null) => {
    if (item?.value) {
      selectGenre(item.value)
    }
  }

  const getDefaultItemFromAnswer = useCallback((): KeyValue | undefined => {
    if (!answer) return

    const { id: value, title: label } = answer
    return {
      value,
      label,
    }
  }, [answer])

  const MovieArtwork = useCallback(() => {
    if (selectedMovie?.artworkUrl) {
      return <Image src={selectedMovie?.artworkUrl} />
    }

    return <Image src={defaultMovieImage} srcWebp={defaultMovieImageWebp} srcAvif={defaultMovieImageAvif} />
  }, [selectedMovie?.artworkUrl])

  return (
    <div className="MovieInput">
      {errorMessage && <div className="Movie-error">{errorMessage}</div>}
      <div className="inputs">
        <Autocomplete
          placeholder={t('movie')}
          items={searchResultItems}
          defaultSelectedItem={getDefaultItemFromAnswer()}
          onInputChange={(title: string) => (title ? searchMovie(title, i18n.language) : selectMovie(selectedMovie))}
          onSelectChange={(item) => onChangeCallback(item?.value)}
        />
        <div className="genre">
          <label>{t('genreLabel')}</label>
          <Select value={selectedGenre} items={genreResultItems} onChange={onClickGenre} placeholder={t('genre')} />
        </div>
      </div>
      <div className="centered-content">
        <MovieArtwork />
      </div>
    </div>
  )
}

export default WithMovie(MovieInput)
